import { MessageSquare } from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import {
  UNAVAILABLE_AGENT_LABEL,
  SessionAgentSlot,
} from "~/pages/manifold-nextgen/components/projects/sessionAgentSlots";
import { AgentAvatar } from "./AgentAvatar";
import { AgentAvatarStack } from "./AgentAvatarStack";

interface SessionAgentIconProps {
  /** `undefined` while the project's agent roster is still loading. */
  slots: SessionAgentSlot[] | undefined;
  size: number;
  /** The sidebar wraps a single slot's icon in a tooltip; the sessions table
   * shows the agent's name as adjacent text instead. Neither applies to zero
   * slots (sidebar: icon only, table: icon + "—") or a stack of two or more
   * (always the combined-tooltip stack). */
  withTooltip: boolean;
}

const PlainIcon = ({ size, withTooltip }: { size: number; withTooltip: boolean }) => (
  <div
    className={cn(
      "flex shrink-0 items-center justify-center",
      withTooltip ? "text-current" : "text-muted-foreground"
    )}
    style={{ width: size, height: size }}
    aria-hidden="true"
  >
    <MessageSquare size={size - 6} />
  </div>
);

/** The icon a project session row renders for the agents that served it,
 * shared by the sidebar's session/featured rows and the sessions tab's
 * browse/search/featured rows so the same ids resolve to the same icons
 * everywhere. A skeleton while the roster loads, the plain chat icon for no
 * agents, one avatar (or the generic icon for an unavailable agent) for a single
 * agent, and a stack for more than one. */
export function SessionAgentIcon({ slots, size, withTooltip }: SessionAgentIconProps) {
  if (slots === undefined) {
    return <Skeleton className="shrink-0 rounded-full" style={{ width: size, height: size }} />;
  }

  if (slots.length > 1) {
    return <AgentAvatarStack slots={slots} size={size} />;
  }

  const slot = slots[0];
  if (!slot) {
    return (
      <>
        <PlainIcon size={size} withTooltip={withTooltip} />
        {!withTooltip && <span className="truncate text-[13px] text-muted-foreground">—</span>}
      </>
    );
  }

  const icon =
    slot.kind === "agent" ? (
      <AgentAvatar agent={slot.agent} size={size} aria-hidden="true" />
    ) : (
      <PlainIcon size={size} withTooltip={withTooltip} />
    );
  const label = slot.kind === "agent" ? slot.agent.display_name : UNAVAILABLE_AGENT_LABEL;

  if (withTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0">{icon}</span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      {icon}
      <span className="truncate text-[13px] text-muted-foreground">{label}</span>
    </>
  );
}
